import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import useCoachGate from './useCoachGate';
import { sha256Hex, PASSWORDS } from './coachPasswords';
import css from './CoachGate.module.scss';

// 教练密码门：包裹 CoachApp，未授权时显示密码输入页
// - 通过校验后渲染 children（即真正的 CoachApp）
// - 通过后顶部会有一个浮动小条「当前教练 · 退出」
//
// 输错 3 次后会弹一个「开发者工具」按钮，里面有个 hash 生成器
// 帮用户算 SHA-256 hash（首次部署时用来填 PASSWORDS 数组）
const CoachGate = ({ children }) => {
  const gate = useCoachGate();

  if (gate.isAuthed) {
    return (
      <>
        {children}
        <GateBadge session={gate.session} onLogout={gate.logout} bypass={gate.gateBypass} />
      </>
    );
  }

  return <LoginScreen gate={gate} />;
};

// ============ 登录页 ============

const LoginScreen = ({ gate }) => {
  const [pwd, setPwd] = useState('');
  const [showDev, setShowDev] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // 进入页面自动聚焦输入框（iPad 上也会自动弹键盘）
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await gate.submitPassword(pwd);
    if (ok) setPwd('');
  };

  return (
    <div className={css.Root}>
      {/* 海克斯科技背景：六边形点阵 + 雾化光晕 */}
      <div className={css.BgGlow} aria-hidden="true" />
      <div className={css.BgGrid} aria-hidden="true" />

      <div className={css.Card}>
        <div className={css.Brand}>
          <div className={css.BrandIcon} aria-hidden="true">
            <svg viewBox="0 0 32 32" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.8">
              <polygon points="16 3 28 10 28 22 16 29 4 22 4 10" />
              <polygon points="16 9 22 12.5 22 19.5 16 23 10 19.5 10 12.5" />
              <circle cx="16" cy="16" r="2" fill="currentColor" stroke="none" />
            </svg>
          </div>
          <div className={css.BrandText}>
            <div className={css.BrandTitle}>LoL Coach</div>
            <div className={css.BrandSub}>战术 BP 推演 · 教练专用</div>
          </div>
        </div>

        <form className={css.Form} onSubmit={handleSubmit}>
          <label className={css.Label}>访问密码</label>
          <input
            ref={inputRef}
            type="password"
            className={cx(css.Input, gate.error && css.InputError)}
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="请输入教练组下发的密码"
            autoComplete="current-password"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            disabled={gate.verifying}
          />
          {gate.error && <div className={css.ErrorMsg}>{gate.error}</div>}

          <button
            type="submit"
            className={css.SubmitBtn}
            disabled={gate.verifying || !pwd}
          >
            {gate.verifying ? '校验中…' : '进入'}
          </button>
        </form>

        <div className={css.Footer}>
          通过后默认免输 30 天 · 不会上传密码到服务器
        </div>

        {/* 多次失败后才显示开发者工具入口（避免在生产环境暴露） */}
        {(gate.attempts >= 3 || showDev) && (
          <button
            type="button"
            className={css.DevToggle}
            onClick={() => setShowDev((v) => !v)}
          >
            {showDev ? '收起开发者工具' : '🔧 开发者工具（密码 hash 生成器）'}
          </button>
        )}

        {showDev && <DevHashTool />}
      </div>
    </div>
  );
};

// ============ Dev 工具：密码 → SHA-256 hash ============

const DevHashTool = () => {
  const [src, setSrc] = useState('');
  const [hash, setHash] = useState('');
  const [copied, setCopied] = useState(false);

  const compute = async () => {
    if (!src) { setHash(''); return; }
    const h = await sha256Hex(src);
    setHash(h);
  };

  useEffect(() => {
    compute();
    setCopied(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const copy = async () => {
    if (!hash) return;
    try {
      await navigator.clipboard.writeText(hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  // 当前 PASSWORDS 数组的使用情况，方便看哪些 slot 是空的
  const usage = PASSWORDS.map((p, i) => ({
    i, label: p.label, configured: Boolean(p.hash),
  }));

  return (
    <div className={css.DevPanel}>
      <div className={css.DevTitle}>密码 hash 生成器</div>
      <div className={css.DevHint}>
        1. 在下方输入你想要的密码<br />
        2. 复制生成的 hash<br />
        3. 把 hash 粘到 <code>src/coach/gate/coachPasswords.js</code> 的 PASSWORDS 数组里<br />
        4. <code>npm run build</code> 重新部署
      </div>

      <input
        type="text"
        className={css.DevInput}
        value={src}
        onChange={(e) => setSrc(e.target.value)}
        placeholder="例如：coach-alpha-2026"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
      />

      <div className={css.DevHashRow}>
        <code className={css.DevHashBox}>{hash || '—'}</code>
        <button
          type="button"
          className={css.DevCopyBtn}
          onClick={copy}
          disabled={!hash}
        >
          {copied ? '✓ 已复制' : '复制'}
        </button>
      </div>

      <div className={css.DevUsage}>
        <div className={css.DevUsageTitle}>当前密码 slot：</div>
        <div className={css.DevUsageList}>
          {usage.map((u) => (
            <div
              key={u.i}
              className={cx(css.DevUsageItem, u.configured && css.DevUsageItemOn)}
            >
              {u.configured ? '●' : '○'} {u.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ 通过后的右上角浮动小条 ============

const GateBadge = ({ session, onLogout, bypass }) => {
  const [open, setOpen] = useState(false);

  // 计算剩余天数
  const daysLeft = session
    ? Math.max(0, Math.ceil((session.expiresAt - Date.now()) / (24 * 3600 * 1000)))
    : 0;

  return (
    <div className={css.Badge}>
      <button
        type="button"
        className={cx(css.BadgeBtn, bypass && css.BadgeBtnWarn)}
        onClick={() => setOpen((v) => !v)}
        title={bypass ? '⚠ 密码门未配置，所有人可访问' : `当前: ${session?.label}`}
      >
        <span className={css.BadgeIcon} aria-hidden="true">
          {bypass ? '⚠' : '👤'}
        </span>
        <span className={css.BadgeLabel}>
          {bypass ? '密码门未启用' : (session?.label || '已授权')}
        </span>
      </button>

      {open && (
        <div className={css.BadgePop}>
          {bypass ? (
            <div className={css.BadgePopWarn}>
              ⚠ 当前没有任何密码 hash<br />
              请在 <code>coachPasswords.js</code> 配置密码<br />
              否则站点等于完全公开
            </div>
          ) : (
            <>
              <div className={css.BadgePopLine}>
                <span className={css.BadgePopK}>当前教练</span>
                <span className={css.BadgePopV}>{session.label}</span>
              </div>
              <div className={css.BadgePopLine}>
                <span className={css.BadgePopK}>授权剩余</span>
                <span className={css.BadgePopV}>{daysLeft} 天</span>
              </div>
              <button
                type="button"
                className={css.BadgePopLogout}
                onClick={() => {
                  if (window.confirm('退出后下次进入需要重新输入密码。继续？')) {
                    onLogout();
                  }
                }}
              >
                退出登录
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CoachGate;
